// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.PermissionStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { action, observable } from 'mobx';
import { get, map, isEmpty, concat, toString, find, pick } from 'lodash/fp';

import fetcher from 'fetchers/Izenda.RoleSetupFetcher';
import tenantDataFetcher from 'fetchers/Izenda.TenantManagementDataFetcher';
import accessFetcher from 'fetchers/Izenda.AccessDataFetcher';
import RolePermission from './models/Izenda.RolePermission';
import PermissionDetail from './models/Izenda.PermissionDetail';
import CategoryAccessibility from './models/Izenda.CategoryAccessibility';
import {
  ROLE_TYPE,
  CATEGORY_TYPE,
  ROLE_PERMISSION_TREE_MAP,
  ROLE_PERMISSION_CONCAT_TREE_MAP
} from 'logic/data/Izenda.PermissionConstants';
import { concatTree, parseToTree, updateTree } from 'share/utils/Izenda.LazyLoadingUtils';

export default class PermissionStore {
  @observable model: any = {};
  @observable tenantId: string = '';
  @observable roleAccessLimits: RolePermission = new RolePermission();
  @observable roleSchedulingLimits: RolePermission = new RolePermission();
  @observable dashboardCategoryAccessibility: any = {};
  @observable reportCategoryAccessibility: any = {};
  @observable tenantDetail: any = null; // For using rolesetup pages

  @observable isShowSystemConfiguration: boolean = true;
  @observable isShowTenantSetup: boolean = true;
  @observable isShowDataSetup: boolean = true;
  @observable isShowUserSetup: boolean = true;
  @observable isShowRoleSetup: boolean = true;
  @observable isShowReports: boolean = true;
  @observable isShowDashboards: boolean = true;
  @observable isShowAccess: boolean = true;
  @observable isShowScheduling: boolean = true;
  @observable isShowEmailing: boolean = true;
  @observable isShowExporting: boolean = true;
  @observable isShowSystemWide: boolean = true;

  constructor(model: any, tenantId: string, isTenantSetup: boolean) {
    this.model = model || new PermissionDetail();
    this.tenantId = tenantId || null;
    this.roleId = null;
    this.dashboardCategoryAccessibility = new CategoryAccessibility(isTenantSetup);
    this.reportCategoryAccessibility = new CategoryAccessibility(isTenantSetup);
    this.isTenantSetup = !!isTenantSetup;
    this.accessLimitsCache = [];
    this.schedulingLimitsCache = [];
  }

  /**
   * Set role detail id
   * @param {string} id
   */
  setRoleDetailId(id: string) {
    this.roleId = id;
  }

  /**
   * Reset data
   */
  @action
  reset() {
    this.accessLimitsCache = [];
    this.schedulingLimitsCache = [];
    this.roleAccessLimits.reset();
    this.roleSchedulingLimits.reset();
  }

  /**
   * get role list for current tenant
   * @method getRoleList
   * @return {promise}
   */
  @action
  getRolePermission(): any {
    const roleAccessLimits = this.roleAccessLimits;
    const roleSchedulingLimits = this.roleSchedulingLimits;
    roleAccessLimits.reset();
    roleSchedulingLimits.reset();

    const readAccessLimit = action(data => {
      roleAccessLimits.readBasicInfo(data);
      roleAccessLimits.readPermissionInfo(this.model.access.accessLimits.value);
    });
    const readSchedulingLimits = action(data => {
      roleSchedulingLimits.readBasicInfo(data);
      roleSchedulingLimits.readPermissionInfo(this.model.scheduling.schedulingLimits.value);
    });

    if (this.isTenantSetup) {
      const readRoleList = action(data => {
        readAccessLimit(data);
        readSchedulingLimits(data);
      });

      return fetcher.getRoleList(this.tenantId).then(readRoleList);
    }

    return Promise.all([
      fetcher.getTypedRole(ROLE_TYPE.ACCESS, this.tenantId).then(readAccessLimit),
      fetcher.getTypedRole(ROLE_TYPE.SCHEDULE, this.tenantId).then(readSchedulingLimits)
    ]);
  }

  /**
   * Update cache data
   * @param  {object} node
   * @param  {object} parent
   * @param  {Array} cache
   */
  updateCacheData(node: any, parent: any, cache: any) {
    if (parent === null) {
      const newItem = pick(['id', 'name', 'checked', 'indeterminate', 'numOfCheckedChilds'], node.source);
      newItem.isVirtual = true;
      newItem.childNodes = [];

      const cachedItem = find({ id: newItem.id }, cache);
      if (cachedItem) {
        cachedItem.checked = newItem.checked;
        cachedItem.indeterminate = newItem.indeterminate;
        cachedItem.numOfCheckedChilds = newItem.numOfCheckedChilds;
      } else {
        cache.push(newItem);
      }
    } else {
      const newItem = pick(['id', 'name', 'checked', 'indeterminate', 'numOfCheckedChilds'], parent.data.source);
      newItem.isVirtual = true;
      const newChildItem = pick(['id', 'name', 'checked', 'indeterminate'], node.source);
      newItem.childNodes = [newChildItem];

      const cachedItem = find({ id: newItem.id }, cache);
      if (cachedItem) {
        cachedItem.checked = newItem.checked;
        cachedItem.indeterminate = newItem.indeterminate;
        cachedItem.numOfCheckedChilds = newItem.numOfCheckedChilds;

        const cachedChildItem = find({ id: newChildItem.id }, cachedItem.childNodes);
        if (cachedChildItem) {
          cachedChildItem.checked = newChildItem.checked;
          cachedChildItem.indeterminate = newChildItem.indeterminate;
        } else {
          cachedItem.childNodes.push(newChildItem);
        }
      } else {
        cache.push(newItem);
      }
    }
  }

  /**
   * update to permission model for access limit data
   * @method updateAccessLimitData
   */
  @action
  updateAccessLimitData(node: any, parent: any, accessModel: any) {
    this.updateCacheData(node, parent, this.accessLimitsCache);
    this.model.accessLimitsTree = this.accessLimitsCache;
    this.model.markDirty();

    const source = node.source;
    if (accessModel && !source.checked) {
      let removeList = [{ id: source.id, name: source.name }];

      if (!isEmpty(node.childNodes)) {
        removeList = concat(removeList, node.childNodes.map(item => pick(['id', 'name'], item.source)));
      }

      accessModel.removeAccessor(removeList);
    }
  }

  /**
   * update to permission model for scheduling limit data
   * @method updateSchedulingLimitsData
   */
  @action
  updateSchedulingLimitsData(node: any, parent: any) {
    this.updateCacheData(node, parent, this.schedulingLimitsCache);
    this.model.schedulingLimitsTree = this.schedulingLimitsCache;
    this.model.markDirty();
  }

  /**
   * Update report accessibility category to permission details
   */
  @action
  updateReportAccessibilityCategory() {
    this.model.reports.reportCategoriesSubcategories.categoryAccessibility.categories = this.reportCategoryAccessibility.prepareDataForSave();
    this.model.markDirty();
  }

  /**
   * Update dashboard accessibility category to permission details
   */
  @action
  updateDashboardAccessibilityCategory() {
    this.model.dashboards.dashboardCategoriesSubcategories.categoryAccessibility.categories = this.dashboardCategoryAccessibility.prepareDataForSave();
    this.model.markDirty();
  }

  /**
   * get category accessibility list for current tenant
   * @param {Object} category
   * @param {string} type
   * @return {Promise}
   */
  @action
  getCategoryAccessibility(category: any, type: string): any {
    const tenantDetail = this.tenantDetail;

    category.reset();

    const readCategoryList = action(data => {
      category.populateData(data);
      let categoryAccessibility, tenantCategoryDetail;
      if (type === CATEGORY_TYPE.REPORT) {
        categoryAccessibility = this.model.reports.reportCategoriesSubcategories.categoryAccessibility.categories;
        tenantCategoryDetail = get(
          'reports.reportCategoriesSubcategories.categoryAccessibility.categories',
          tenantDetail
        );
      } else {
        categoryAccessibility = this.model.dashboards.dashboardCategoriesSubcategories.categoryAccessibility.categories;
        tenantCategoryDetail = get(
          'dashboards.dashboardCategoriesSubcategories.categoryAccessibility.categories',
          tenantDetail
        );
      }

      category.readCategoryAccessibility(categoryAccessibility, tenantCategoryDetail);
    });

    if (this.isTenantSetup) {
      return tenantDataFetcher.getCategoryAccessibility(type).then(readCategoryList);
    }

    return fetcher.getTypedCategory(type, this.tenantId).then(readCategoryList);
  }

  /**
   * Build param
   * @param {string} type
   */
  buildParam(type: string): any {
    return {
      type,
      tenantId: this.tenantId,
      skipItems: 0,
      pageSize: -1,
      parentIds: [],
      defaultChecked: false,
      isUncategorized: false
    };
  }

  /**
   * Load lazy category accessibility list for current tenant
   * @param {Object} category
   * @param {string} type
   * @return {Promise}
   */
  @action
  loadLazyCategory(category: any, type: string): any {
    const param = this.buildParam(type);

    category.reset();

    const readCategoryList = action(data => {
      category.populateData(data.result);
      let categoryAccessibility;
      if (type === CATEGORY_TYPE.REPORT) {
        categoryAccessibility = this.model.reports.reportCategoriesSubcategories.categoryAccessibility.categories;
      } else {
        categoryAccessibility = this.model.dashboards.dashboardCategoriesSubcategories.categoryAccessibility.categories;
      }

      category.readCategoryAccessibility(categoryAccessibility);
    });

    return fetcher.loadLazyTypedCategory(param).then(readCategoryList);
  }

  /**
   * Get param lazyload
   * @param  {Object} pageInfo
   * @param  {string} searchKey
   * @return {Object}
   */
  getParamLazyload(pageInfo: any, searchKey: string): any {
    return {
      roleId: this.roleId,
      tenantId: this.tenantId,
      skipItems: pageInfo.fromIndex,
      pageSize: pageInfo.pageSize,
      parentIds: pageInfo.parentIds,
      criteria: [
        {
          key: 'name',
          value: searchKey
        }
      ]
    };
  }

  /**
   * Load partial role permission
   * @param  {Array}  roleLimits
   * @param  {Fucntion}  fetcherFn
   * @param  {Object}  pageInfo
   * @param  {boolean} isReset
   * @param  {string}  searchKey
   * @return {Object}
   */
  @action
  async loadPartialRolePermission(dataInfo: any, pageInfo: any, isReset: boolean, searchKey: string) {
    const { roleLimits, cacheData, loadPartialData } = dataInfo;
    searchKey = searchKey || roleLimits.filterText;
    pageInfo = pageInfo || roleLimits.lazyLoadingStore.pageInfoForFirstTime;
    const params = this.getParamLazyload(pageInfo, searchKey);

    const { isLastPage, childNodes, numOfChilds } = await loadPartialData(params);

    const newData = roleLimits.convertData(childNodes);
    roleLimits.isLastPage = isLastPage;
    if (isReset) {
      roleLimits.roles = [];
      roleLimits.displayData = [];
    }

    if (toString(searchKey) === toString(roleLimits.filterText)) {
      concatTree(roleLimits.roles, newData, ROLE_PERMISSION_CONCAT_TREE_MAP, numOfChilds, []);
      updateTree(roleLimits.roles, cacheData, ROLE_PERMISSION_TREE_MAP, [
        'checked',
        'indeterminate',
        'numOfCheckedChilds'
      ]);
      roleLimits.displayData = parseToTree(roleLimits.roles, ROLE_PERMISSION_TREE_MAP);
    }

    return roleLimits;
  }

  /**
   * Load partial permission access
   * @param  {Object}  pageInfo
   * @param  {boolean} isReset
   * @param  {string}  searchKey
   * @return {Promise}
   */
  @action
  loadPartialPermissionAccess(pageInfo: any, isReset: boolean, searchKey: string): any {
    const fetcherFn = this.isTenantSetup ? tenantDataFetcher : fetcher;
    const dataInfo = {
      roleLimits: this.roleAccessLimits,
      cacheData: this.accessLimitsCache,
      loadPartialData: fetcherFn.loadPartialAccessLimits
    };

    return this.loadPartialRolePermission(dataInfo, pageInfo, isReset, searchKey);
  }

  /**
   * Load partial permission schedule
   * @param  {Object}  pageInfo
   * @param  {boolean} isReset
   * @param  {string}  searchKey
   * @return {Promise}
   */
  @action
  loadPartialPermissionSchedule(pageInfo: any, isReset: boolean, searchKey: string): any {
    const fetcherFn = this.isTenantSetup ? tenantDataFetcher : fetcher;
    const dataInfo = {
      roleLimits: this.roleSchedulingLimits,
      cacheData: this.schedulingLimitsCache,
      loadPartialData: fetcherFn.loadPartialScheduleLimits
    };
    return this.loadPartialRolePermission(dataInfo, pageInfo, isReset, searchKey);
  }

  /**
   * Get request param for available roles or users
   * @param  {Object} pageInfo
   * @return {Object}
   */
  getRequestParam(pageInfo: any): any {
    return {
      roleId: this.roleId,
      tenantId: this.tenantId,
      localTree: this.accessLimitsCache,
      criteria: pageInfo.searchCriteria || [],
      pageIndex: pageInfo.pageIndex || 1,
      pageSize: pageInfo.pageSize || 10,
      sortOrders: pageInfo.sortOrders || [
        {
          key: 'name',
          descending: true
        }
      ]
    };
  }

  /**
   * Load users for role
   * @param  {Object} pageInfo
   * @return {Promise}
   */
  loadAvailableUsers(pageInfo: any = {}): any {
    const requestData = this.getRequestParam(pageInfo);

    return accessFetcher.loadUsersAccess(requestData);
  }

  /**
   * Load users for role
   * @param  {Object} pageInfo
   * @return {Promise}
   */
  loadAvailableRoles(pageInfo: any = {}): any {
    const requestData = this.getRequestParam(pageInfo);

    return accessFetcher.loadRolesAccess(requestData);
  }

  /**
   * Toggle all
   * @param {boolean} isShow
   */
  @action
  toggleAll(isShow: boolean) {
    this.isShowSystemConfiguration = isShow;
    this.isShowTenantSetup = isShow;
    this.isShowDataSetup = isShow;
    this.isShowUserSetup = isShow;
    this.isShowRoleSetup = isShow;
    this.isShowReports = isShow;
    this.isShowDashboards = isShow;
    this.isShowAccess = isShow;
    this.isShowScheduling = isShow;
    this.isShowEmailing = isShow;
    this.isShowExporting = isShow;
    this.isShowSystemWide = isShow;
  }

  /**
   * Update tenant detail
   * @param {any} tenantDetail
   */
  updateTenantDetail(tenantDetail: any) {
    this.tenantDetail = tenantDetail;
  }
}



// WEBPACK FOOTER //
// ./ui/logic/Izenda.PermissionStore.js